const DIVIDER = "─".repeat(56);

const longDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const shortDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });
};

const clockTime = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
};

const pretty = (s) => (s || "").replace(/_/g, " ");

const wrap = (text, width = 76, indent = "") =>
  (text || "")
    .split("\n")
    .flatMap((line) => {
      if (line.length <= width) return [indent + line];
      const out = [];
      let current = "";
      for (const word of line.split(" ")) {
        if ((current + " " + word).trim().length > width) {
          out.push(indent + current.trim());
          current = word;
        } else {
          current += " " + word;
        }
      }
      if (current.trim()) out.push(indent + current.trim());
      return out;
    })
    .join("\n");

const bullets = (items, label) => {
  const list = (items || []).filter(Boolean);
  if (!list.length) return "";
  const rows = list.map((i) => {
    const wrapped = wrap(i, 70, "      ");
    return "    - " + wrapped.slice(6);
  });
  return `  ${label}\n${rows.join("\n")}\n`;
};

const entryBlock = (entry) => {
  const lines = [];
  lines.push(`${longDate(entry.created_at)} at ${clockTime(entry.created_at)}`);
  if ((entry.moods || []).length) lines.push(`Mood: ${entry.moods.map(pretty).join(", ")}`);
  if (entry.body_signal) lines.push(`Body: ${pretty(entry.body_signal)}`);
  if (entry.updated_at) lines.push(`(edited ${shortDate(entry.updated_at)})`);
  lines.push("");
  lines.push(wrap(entry.content));

  const r = entry.reflection;
  if (r && (r.mirror || (r.emotions || []).length || (r.patterns || []).length)) {
    lines.push("");
    lines.push("  WHAT THE MIRROR NOTICED");
    if ((r.emotions || []).length) lines.push(wrap(`Emotions: ${r.emotions.join(", ")}`, 72, "  "));
    if (r.mirror) {
      lines.push("");
      lines.push(wrap(r.mirror, 72, "  "));
    }
    const p = bullets(r.patterns, "Patterns:");
    const q = bullets(r.gentle_questions, "Questions to sit with:");
    if (p) lines.push("", p.trimEnd());
    if (q) lines.push("", q.trimEnd());
  }
  return lines.join("\n");
};

export const buildReadableExport = (data) => {
  const p = data.profile || {};
  const entries = data.entries || [];
  const payments = data.payments || [];
  const out = [];

  out.push("CLARITY MIRROR");
  out.push(`Your writing, exported on ${longDate(data.exported_at)}`);
  out.push("");
  out.push(`Prepared for ${p.name || "you"}${p.email ? ` (${p.email})` : ""}`);
  if (p.created_at) out.push(`Writing here since ${shortDate(p.created_at)}`);
  if (p.plan) out.push(`Plan: ${pretty(p.plan)}`);
  if (p.access_until) out.push(`Access until ${shortDate(p.access_until)}`);
  out.push(`${entries.length} ${entries.length === 1 ? "entry" : "entries"}`);
  out.push("");
  out.push(
    wrap(
      "Everything you have ever written here is below, oldest first, along with the reflection the mirror wrote back to you. On our servers your entries are encrypted with a key unique to your account. This file is the readable copy, and it is yours to keep."
    )
  );
  out.push("");

  if (!entries.length) {
    out.push(DIVIDER);
    out.push("");
    out.push("You haven't written an entry yet. Whenever you're ready, a single line is enough.");
    out.push("");
  }

  entries.forEach((entry) => {
    out.push(DIVIDER);
    out.push("");
    out.push(entryBlock(entry));
    out.push("");
  });

  if (payments.length) {
    out.push(DIVIDER);
    out.push("");
    out.push("YOUR PAYMENTS");
    out.push("");
    payments.forEach((pay) => {
      out.push(
        `${shortDate(pay.created_at)} · ${pretty(pay.plan)} · $${Number(pay.amount || 0).toFixed(2)} · ${pretty(pay.status)}`
      );
    });
    out.push("");
    out.push("We never receive, store, or see your card number. Payments are handled by Stripe.");
    out.push("");
  }

  out.push(DIVIDER);
  out.push("");
  out.push("That's everything we hold about you. Thank you for writing here.");
  out.push("");
  return out.join("\n");
};
